import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';

// One VAL request per module/entity, shared by every cell in the grid
const valMsgCache = new Map();

const valMsgsFor = (rsrc, module, entity) => {
  const key = `${module}/${entity}`;
  if (!valMsgCache.has(key)) {
    const promise = rsrc
      .rows(module, 'VAL', { expr: ExprUtils.whereParams('entity', entity) })
      .then((vals) => Object.fromEntries(vals.map((val) => [val.code, val.msg])));
    valMsgCache.set(key, promise);
  }
  return valMsgCache.get(key);
};

// Mirrors ValRegistry.valInstance: $(key) comes from the Val's params, %(key) from the entity's fields
const formatMsg = (template, params, data) =>
  template
    .replace(/\$\((\w+)\)/g, (_, key) => params?.[key] ?? '')
    .replace(/%\((\w+)\)/g, (_, key) => data?.[key] ?? '');

/**
 * Renders val_res as messages when the fieldDef passes the row's entity through cellRendererParams
 * (e.g. cellRendererParams: { entity: 'DIR_SALE_LINE' }); falls back to the val codes otherwise.
 * A val instance with a "summary" param shows that in the cell; the full message goes to the tooltip.
 */
export const ValResCellRenderer = ({ data, entity }) => {
  const rsrc = useRsrc();
  const module = useModuleFromRoute();
  const [codeToMsg, setCodeToMsg] = useState();

  useEffect(() => {
    if (!entity) return;
    valMsgsFor(rsrc, module, entity).then(setCodeToMsg);
  }, [module, entity]);

  if (!data?.valRes) return <span />;

  let valRes;
  try {
    valRes = JSON.parse(data.valRes);
  } catch {
    return <span />;
  }

  const cellMsgs = [];
  const fullMsgs = [];
  for (const [code, value] of Object.entries(valRes)) {
    const template = codeToMsg?.[code];
    if (!template) {
      cellMsgs.push(code);
      fullMsgs.push(code);
      continue;
    }
    // A val's value is either a list of param maps (one per instance) or a single param map
    const instances = Array.isArray(value) ? value : [value];
    for (const params of instances) {
      const msg = formatMsg(template, params, data);
      cellMsgs.push(params?.summary ?? msg);
      fullMsgs.push(msg);
    }
  }
  if (cellMsgs.length === 0) return <span />;

  return (
    <Tooltip title={fullMsgs.join('; ')}>
      <span>{cellMsgs.join('; ')}</span>
    </Tooltip>
  );
};
